import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">您查找的文章可能已被移除或不存在。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              回到首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`女女,${article.tags.join(',')},女女情感故事,女女关系`}
        canonicalUrl={`https://tazhijian.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.coverImage}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishedAt,
          publisher: {
            '@type': 'Organization',
            name: '她之间',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://tazhijian.com/article/${article.slug}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tazhijian.com' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `https://tazhijian.com/category/${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://tazhijian.com/article/${article.slug}` },
          ],
        }}
      />

      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-3xl mx-auto">
            <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-6 md:mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 女女情感故事`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                <i className="ri-user-line mr-1" />
                {article.author}
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                <i className="ri-calendar-line mr-1" />
                {article.publishedAt}
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                <i className="ri-time-line mr-1" />
                {article.readTime}分钟阅读
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>

            <div
              className="article-content prose prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-600 whitespace-nowrap">分享到：</span>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-base" />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-base" />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-base" />
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}